/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, no-param-reassign, react/no-unescaped-entities, react/prop-types */
import { api } from "jiffy-api";
import { isElementVisible } from "common/utils/ui/visibility";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import {
  $fileInputAtom,
  $modalAtom,
  designsAtom,
  errorAtom,
  intervalIdAtom,
  loadingAtom,
  settingsAtom,
  uploadingAtom
} from "./canvaModule.atoms";

const canvaModule = {
  namespaced: true,

  state() {
    return {
      $fileInput: $fileInputAtom,
      $modal: $modalAtom,
      designs: designsAtom,
      error: errorAtom,
      intervalId: intervalIdAtom,
      loading: loadingAtom,
      settings: settingsAtom,
      uploading: uploadingAtom
    };
  },

  getters: {
    $fileInput: (state: any) => state.$fileInput,
    $modal: (state: any) => state.$modal,
    continuation: (state: any) => state.designs.continuation,
    designs: (state: any) => state.designs.items,
    error: (state: any) => state.error,
    intervalId: (state: any) => state.intervalId,
    loading: (state: any) => state.loading,
    settings: (state: any) => state.settings,
    uploading: (state: any) => state.uploading
  },

  mutations: {
    $setFileInput(state: any, $fileInput: any) {
      state.$fileInput = $fileInput;
    },
    $setModal(state: any, $modal: any) {
      state.$modal = $modal;
    },
    setError(state: any, error: any) {
      state.error = "Something went wrong, please try again.";

      try {
        const match = error.match(/\{.*\}/);
        const jsonError = JSON.parse(match[0]);
        state.error = `Error: ${jsonError.message}`;
      } catch (_) {
        console.error(error);
      }
    },
    setDesigns(state: any, designs: any) {
      state.designs = {
        continuation: designs.continuation,
        items: [...state.designs.items, ...designs.items]
      };
    },
    clearDesigns(state: any) {
      state.designs = {
        continuation: undefined,
        items: []
      };
    },
    clearError(state: any) {
      state.error = null;
    },
    setIntervalId(state: any, id: any) {
      state.intervalId = id;
    },
    setSettings(state: any, settings: any) {
      state.settings = settings;
    },
    toggleLoading(state: any, force: any) {
      state.loading = force;
    },
    toggleUploading(state: any, force: any) {
      state.uploading = force;
    }
  },

  actions: {
    async checkDesignStatus({ commit, dispatch, getters }: any, jobId: any) {
      const modalVisible = isElementVisible(getters.$modal);
      if (!modalVisible) {
        commit("toggleUploading", false);
        clearInterval(getters.intervalId);
        return;
      }

      try {
        const { job } = await api.canva.designStatus({ jobId });

        if (job.status === "success" && modalVisible) {
          clearInterval(getters.intervalId);
          await dispatch("setImageFromURL", job.urls[0]);
        } else if (!modalVisible) {
          clearInterval(getters.intervalId);
        }
      } catch (e) {
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        commit("setError", e.message);
      } finally {
        commit("toggleUploading", false);
      }
    },
    async fetchDesigns({ commit }: any, continuation: any) {
      commit("toggleLoading", true);

      try {
        const params = continuation ? { continuation } : {};
        // @ts-expect-error TS migration
        const designs = await api.canva.designs(params);
        commit("setDesigns", designs);
      } catch (e) {
        /*
         * 401 is returned when the token is invalid or expired
         * We need to reload the page to get a new token
         */
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        if (e.status === 401) {
          window.location.reload();
          return;
        }

        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        commit("setError", e.message);
      } finally {
        commit("toggleLoading", false);
      }
    },
    async fetchSettings({ commit }: any, namespace: any) {
      try {
        const settings = await api.canva.settings({
          source: namespace,
          // @ts-expect-error TS migration
          redirect_url: window.location.pathname
        });
        commit("setSettings", settings);
      } catch (e) {
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        commit("setError", e.message);
      }
    },
    async selectDesign({ commit, dispatch }: any, designId: any) {
      commit("toggleUploading", true);

      submitDtfEvent("canva_design_selected", { design_id: designId });

      try {
        const { job } = await api.canva.selectDesign({ designId });
        const intervalId = setInterval(() => {
          dispatch("checkDesignStatus", job.id);
        }, 1000);
        commit("setIntervalId", intervalId);
      } catch (e) {
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        commit("setError", e.message);
        commit("toggleUploading", false);
      }
    },
    async setImageFromURL({ commit, getters }: any, url: any) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();

        const changeEvent = new Event("change");
        const urlPath = new URL(url).pathname;
        const fileName = urlPath.substring(urlPath.lastIndexOf("/") + 1);
        const file = new File([blob], fileName, { type: blob.type });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        getters.$fileInput.files = dataTransfer.files;
        getters.$fileInput.dispatchEvent(changeEvent);
        getters.$fileInput.setAttribute("data-upload-source", "canva");

        // @ts-expect-error TS(2304): Cannot find name 'jQuery'.
        jQuery(getters.$modal).modal("hide");
      } catch (e) {
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        commit("setError", e.message);
      }
    },
    resetModalState({ commit, dispatch, getters }: any) {
      commit("toggleUploading", false);
      commit("toggleLoading", false);

      if (getters.error) {
        commit("clearError");
        commit("clearDesigns");
        dispatch("fetchDesigns");
      }
    }
  }
};

export default canvaModule;
